import { useMemo } from 'react';
import {
  FilterBar,
  ListEmpty,
  MoreInOtherMonths,
  NumberedPager,
  useListUrlState,
  rangeForPreset,
  windowLabel,
  inRange,
  applySearch,
  sortBy,
  paginate,
  DEFAULT_HISTORY_PRESET,
  PAYERS,
  type HistoryDatePreset,
} from '@/components/lists';
import { CLOSED_HISTORY, CLOSED_STATUS_OPTIONS, type ClosedRow } from '../historyMock';
import { money } from '../utils';
import { CardRow } from './CardRow';

/**
 * Denied & underpaid › Closed (rcm-18). Replaces the old split between
 * Denied/Underpaid's "Resolved" link (`DeniedResolved`) and Appeals sent's
 * "Closed" archive table (`AppealsSentArchive`): one pill, one list, the same
 * card-row shape as the open queues so every claim's patient, amount, payer,
 * codes, outcome and date are easy to scan (not a cramped table). Filter bar
 * (search · date range · payer · status) + numbered pages of 50, same as the
 * two lists it replaces. `liveClosed` folds in appeals closed via Decide this
 * session (store-held `AppealSent` rows, not part of the static mock).
 */
export function ClosedHistory({ liveClosed = [] }: { liveClosed?: ClosedRow[] }) {
  const [s, patch] = useListUrlState({
    q: '',
    from: DEFAULT_HISTORY_PRESET,
    to: '',
    payer: '',
    status: '',
    page: '1',
  });

  const preset = (s.from || DEFAULT_HISTORY_PRESET) as HistoryDatePreset;
  const page = Math.max(1, Number.parseInt(s.page, 10) || 1);

  const rows = useMemo(
    () => (liveClosed.length > 0 ? [...liveClosed, ...CLOSED_HISTORY] : CLOSED_HISTORY),
    [liveClosed],
  );

  const result = useMemo(() => {
    const range = rangeForPreset(preset);
    const searched = applySearch(
      rows,
      s.q,
      (r) => `${r.patient} ${r.id} ${r.payer} ${r.codes} ${String(Math.round(r.billed))} ${r.statusLabel}`,
    );
    const filtered = searched.filter(
      (r) => (!s.payer || r.payer === s.payer) && (!s.status || r.statusValue === s.status),
    );
    const inWindow = sortBy(
      filtered.filter((r) => inRange(r.dateIso, range)),
      (r) => r.dateIso,
      -1,
    );
    const outside = filtered.length - inWindow.length;
    return { inWindow, outside };
  }, [rows, preset, s.q, s.payer, s.status]);

  const total = result.inWindow.length;
  const pageRows = paginate(result.inWindow, page, 50);
  const label = windowLabel(preset);

  return (
    <div className="apl-sent-page">
      <FilterBar
        search={s.q}
        onSearch={(v) => { patch({ q: v, page: '1' }); }}
        searchPlaceholder="Search patient, claim, payer or amount"
        dateRange={preset}
        onDateRange={(v) => { patch({ from: v, page: '1' }); }}
      >
        <select
          className="lx-select"
          value={s.payer}
          aria-label="Payer"
          onChange={(e) => { patch({ payer: e.target.value, page: '1' }); }}
        >
          <option value="">All payers</option>
          {PAYERS.map((p) => (
            <option key={p} value={p}>{p}</option>
          ))}
        </select>
        <select
          className="lx-select"
          value={s.status}
          aria-label="Status"
          onChange={(e) => { patch({ status: e.target.value, page: '1' }); }}
        >
          <option value="">Any status</option>
          {CLOSED_STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </FilterBar>

      <section className="apl-card" aria-labelledby="apl-closed-title">
        <div className="apl-card-head">
          <div>
            <h2 className="apl-h2" id="apl-closed-title">Closed</h2>
            <span className="apl-meta">
              Denied and underpaid claims that are finished, and appeals that reached a final answer. Showing {label}.
            </span>
          </div>
        </div>

        {total === 0 ? (
          <>
            <ListEmpty
              {...(s.q ? { query: s.q } : {})}
              windowLabel={label}
              {...(preset !== 'all-time'
                ? { onSearchAllTime: () => { patch({ from: 'all-time', page: '1' }); } }
                : {})}
              {...(!s.q && preset === 'all-time' ? { message: 'Nothing closed yet.' } : {})}
            />
            {preset !== 'all-time' && result.outside > 0 ? (
              <MoreInOtherMonths count={result.outside} onShowAllTime={() => { patch({ from: 'all-time', page: '1' }); }} />
            ) : null}
          </>
        ) : (
          <>
            <div className="apl-qlist">
              {pageRows.map((r) => (
                <CardRow
                  key={r.id}
                  href={`/claims/record/${r.id}`}
                  name={r.patient}
                  amount={money(r.billed)}
                  sub={`${r.payer} · ${r.codes}`}
                  tag={{ label: r.statusLabel, cls: r.statusCls }}
                  metaLine={r.metaLine}
                  a11yLabel={`${r.patient}, ${money(r.billed)}, ${r.payer}, ${r.codes}, ${r.statusLabel}`}
                  {...(r.recovered > 0 ? { taglineText: `Recovered ${money(r.recovered)}` } : {})}
                />
              ))}
            </div>

            <NumberedPager page={page} total={total} onPage={(p) => { patch({ page: String(p) }); }} />
            {preset !== 'all-time' && result.outside > 0 ? (
              <MoreInOtherMonths count={result.outside} onShowAllTime={() => { patch({ from: 'all-time', page: '1' }); }} />
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}
